import * as React from 'react';
import classNames from 'classnames';
import { assign as _assign, difference as _difference, includes as _includes } from 'lodash';
import { connect } from 'react-redux';
import { GlobalPrivateState } from 'src/api';
import { LEFT_RIGHT, BuiltInPartsModel, BuiltInPartsDefaultModel, ConfigurationModel, ApronBackSplashModel } from 'franke-trough-dependencies';
import { requestAPIActions } from 'loopback-react-request';

import { ApronRequired } from '../ApronBacksplash';

import { FormattedMessage } from 'react-intl';
import MESSAGES from './Recesses.messages';
import { isConfigurationEditable } from 'src/redux/utils.redux';

export interface TowelRailProps {
  configuration: GlobalPrivateState['configuration'];
  dispatch: any;
}

export interface TowelRailState {
  towelRail: boolean;
  towelRailPosition: null | LEFT_RIGHT[];
}

export class TowelRailPartial extends React.Component<TowelRailProps, TowelRailState> {
  constructor(props: TowelRailProps) {
    super(props);
    this.state = {
      towelRail: false,
      towelRailPosition: null,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handlePositionChange = this.handlePositionChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      towelRail: this.towelRail,
      towelRailPosition: this.towelRailPosition || [],
    });
  }

  componentDidUpdate(prevProps: TowelRailProps) {
    const configuration = this.configuration;
    const prevConfiguration = prevProps.configuration && prevProps.configuration.result;
    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      if (this.towelRail !== this.state.towelRail) {
        this.setState({
          towelRail: this.towelRail,
        });
      }
      if (
        this.towelRailPosition && 
        this.state.towelRailPosition && 
        _difference(this.towelRailPosition, this.state.towelRailPosition)
      ) {
        this.setState({
          towelRailPosition: this.towelRailPosition,
        });
      }
    }
  }

  public render() {
    const { towelRail } = this.state;

    return (
      <div className="partial-towel-rail position-relative pb-4">
        {this.towelRailDefaults && this.towelRailDefaults.allValues && this.towelRailDefaults.allValues.map((value, index) => {
          const key = `towel_rail_${index}`;
          const disabled = !isConfigurationEditable() || (value === true && this.apronRequired) || !(_includes(this.towelRailDefaults && this.towelRailDefaults.values, value));
          
          return (
            <div key={key} className="col-12">
              <label
                className={classNames({
                  disabled: disabled,
                })}
              >
                {!value ? (
                  <span>
                    <FormattedMessage {...MESSAGES.towelRailWithout} />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage {...MESSAGES.towelRailWith} />
                  </span>
                )}
                <input 
                  className={classNames({
                    disabled: disabled,
                  })}
                  checked={towelRail === value}
                  disabled={disabled}
                  onChange={this.handleChange}
                  name="towelRail"
                  type="radio"
                  value={value.toString()}
                />
                <span className="checkmark" />
              </label>
              {value && <ApronRequired id={`towel_rail`} />}
            </div>
          );
        })}
      </div>
    );
  }

  private get apron(): ApronBackSplashModel['apron'] | null | undefined {
    return this.configuration &&
      this.configuration.apronBackSplash &&
      this.configuration.apronBackSplash.apron;
  }

  private get apronHeight(): ApronBackSplashModel['apronHeight'] | null | undefined {
    return this.configuration &&
      this.configuration.apronBackSplash &&
      this.configuration.apronBackSplash.apronHeight;
  }

  private get apronRequired(): boolean {
    return this.apron === 'without' ||
      (this.apron === 'with' && !this.apronHeight) || 
      (this.apron === 'with' && (this.apronHeight && this.apronHeight < 150)) ? true : false;
  }

  private get configuration(): ConfigurationModel | undefined {
    return this.props.configuration && this.props.configuration.result;
  }

  private dispatchConfiguration(props: TowelRailState) {
    if (!this.configuration || !this.configuration.builtInParts) {
      return;
    }

    const builtInParts: BuiltInPartsModel = this.configuration.builtInParts;
    const data: BuiltInPartsModel = _assign({}, builtInParts, {
      towelRail: props.towelRail,
      towelRailPosition: props.towelRailPosition || null,
    });

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${this.configuration && this.configuration.id}`,
        options: {
          method: 'PATCH',
          data: {builtInParts: data}
        },
      },
      true,
    ));
  }

  private handleChange(event: React.SyntheticEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const towelRail = target.value === 'true' && true || target.value === 'false' && false;
    const state = {
      towelRail,
      towelRailPosition: towelRail === true ? this.state.towelRailPosition : null,
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private handlePositionChange(positions: LEFT_RIGHT[]) {
    const towelRailPosition = positions;
    const state = {
      towelRail: this.state.towelRail,
      towelRailPosition
    };
    this.setState(state);
    this.dispatchConfiguration(state);
  }

  private get towelRail(): BuiltInPartsModel['towelRail'] {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.towelRail ? this.configuration.builtInParts.towelRail : false;
  }

  private get towelRailPosition(): BuiltInPartsModel['towelRailPosition'] | null | undefined {
    return this.configuration &&
      this.configuration.builtInParts &&
      this.configuration.builtInParts.towelRailPosition;
  }

  private get towelRailDefaults(): BuiltInPartsDefaultModel['towelRail'] | undefined {
    return this.configuration &&
      this.configuration.defaults &&
      this.configuration.defaults.builtInParts &&
      this.configuration.defaults.builtInParts.towelRail;
  }
}

export const TowelRail = connect((state: GlobalPrivateState) => ({
  configuration: state.configuration,
}))(TowelRailPartial);

export default TowelRail;